/**
 * Tiny "cost: N fierros" hint shown next to export buttons.
 *
 * Returns an empty fragment if the user is on an active subscription, since
 * there's nothing to pay. Use as a sibling of the button:
 *
 *   <Button>Exportar PDF</Button>
 *   <ExportCostHint cost={2} />
 */

import { Infinity as InfinityIcon } from 'lucide-react';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { useAuthStore } from '@/stores/authStore';

function isSubscriptionActive(
  status: string | undefined,
  expiresAt: string | null | undefined,
): boolean {
  if (status !== 'active' || !expiresAt) return false;
  return new Date(expiresAt).getTime() > Date.now();
}

function hasUnlimitedRole(perms: string[] | undefined, slug: string | undefined): boolean {
  if (slug === 'subscriber') return true;
  return (perms ?? []).includes('billing.unlimited');
}

export function ExportCostHint({
  cost,
  className,
}: {
  cost: number;
  className?: string;
}) {
  const user = useAuthStore((s) => s.user);
  const sub =
    isSubscriptionActive(user?.subscription_status, user?.subscription_expires_at) ||
    hasUnlimitedRole(user?.role?.permissions, user?.role?.slug);

  if (sub) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10.5px] ${className ?? ''}`}
        style={{ color: 'var(--accent)' }}
        title="Suscripción activa — exportación incluida"
      >
        <InfinityIcon size={10} strokeWidth={2} aria-hidden /> incluido
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 text-[10.5px] ${className ?? ''}`}
      style={{ color: 'var(--text-muted)' }}
      title={`Esta exportación cuesta ${cost} ${cost === 1 ? 'fierro' : 'fierros'}`}
    >
      <FierroIcon size={10} />
      {cost}
    </span>
  );
}
